/**
 * InvoiceAdjustFields — Ted #3 + #9 (#906 #912), lifted for Ted #22 (#983).
 *
 * The "Adjust price" form. It lived inside SettleNowDialog, which is the one
 * place it could be reached — so the moment Ted dismissed Settle Now, the
 * discount he went back for was unreachable from anywhere. This component is
 * the form itself; the PARENT owns the API call, because the two call sites
 * do different things with the result (SettleNowDialog re-reads the whole
 * cart; Open Invoices reloads its queue).
 *
 * Design philosophy: structural `--ferro-*` tokens only (it inherits the
 * dialog's and the queue's own classes), the five states are all present
 * (idle / open / saving / error / applied-and-closed), and every control is
 * a real button or input — no hover-only affordances.
 */
import type { AdminInvoiceAdjustmentInput } from '../../lib/api.js';

function formatMoney(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

export type AdjustKind = AdminInvoiceAdjustmentInput['kind'];

const KINDS: ReadonlyArray<{ value: AdjustKind; label: string }> = [
  { value: 'percent', label: 'Percent (− for a discount)' },
  { value: 'amount', label: 'Amount (− for a discount)' },
  { value: 'override', label: 'New total' },
];

export interface InvoiceAdjustFieldsProps {
  /**
   * Test-id and aria prefix, so each call site keeps its own stable hooks
   * (`settle-now-adjust-*` on the dialog, `open-invoice-adjust-<id>-*` in
   * the queue) and neither site's existing tests move.
   */
  readonly idPrefix: string;
  readonly kind: AdjustKind;
  readonly value: string;
  readonly reason: string;
  readonly saving: boolean;
  readonly error: string | null;
  /** Rendered under the inputs — e.g. the cart-wide percent warning. */
  readonly hint?: string | null;
  /**
   * Ted #3 follow-up (#906), carried through the Ted #22 lift. When the call
   * site knows what a percent would apply to, it says so out loud and the
   * $0 refusals become visible BEFORE the round trip. Omit it and the form
   * behaves exactly as it did before #906 (the Open Invoices queue does not
   * compute the desk-band authority the locked state needs).
   */
  readonly amountDueCents?: number | null;
  /** The noun the amount-due line and the $0 note use ('visit' / 'booking'). */
  readonly subject?: string;
  /**
   * $0 AND no authority past the desk band ⇒ every path is refused, so the
   * whole form is disabled with the reason said.
   */
  readonly locked?: boolean;
  /** $0 on purpose (a comp, evidenced by an adjustment ledger) vs never priced. */
  readonly deliberatelyZero?: boolean;
  /**
   * The hint's test-id, when it is not `${idPrefix}-hint`. SettleNowDialog's
   * cart warning shipped as `settle-now-adjust-cart-hint` BEFORE this lift,
   * and it keeps that id — review fix (2026-09-16): the lift silently
   * renamed it, which made "the markup and test-ids are unchanged" untrue
   * even though nothing referenced the old id yet.
   */
  readonly hintTestId?: string;
  /**
   * Where the form is mounted. `dialog` (default) keeps SettleNowDialog's
   * existing full-width stacked layout byte-for-byte; `inline` is the
   * compact, wrapping layout a queue ROW needs — a dialog's vertical button
   * stack inside a list row pushes the row's own controls off the line.
   */
  readonly variant?: 'dialog' | 'inline';
  readonly onKindChange: (kind: AdjustKind) => void;
  readonly onValueChange: (value: string) => void;
  readonly onReasonChange: (reason: string) => void;
  readonly onApply: () => void;
  readonly onCancel: () => void;
}

/**
 * The server refuses an adjustment that leaves the amount unchanged, and a
 * reason is mandatory. Both are enforced here too so the desk sees a
 * disabled button rather than a round-trip and a red box.
 */
export function isAdjustReady(
  kind: AdjustKind,
  value: string,
  reason: string,
  opts: { readonly zeroAmountDue?: boolean; readonly locked?: boolean } = {},
): boolean {
  const n = Number(value);
  return (
    !opts.locked &&
    reason.trim().length >= 2 &&
    value.trim() !== '' &&
    Number.isFinite(n) &&
    (kind === 'override' ? n >= 0 : n !== 0) &&
    // $0 due: a percent changes nothing and a decrease (or a New total of $0)
    // goes below zero / changes nothing. The server refuses all three, so the
    // button never offers them.
    !(opts.zeroAmountDue && (kind === 'percent' || n <= 0))
  );
}

export function InvoiceAdjustFields({
  idPrefix,
  kind,
  value,
  reason,
  saving,
  error,
  hint = null,
  amountDueCents = null,
  subject = 'visit',
  locked = false,
  deliberatelyZero = false,
  hintTestId,
  variant = 'dialog',
  onKindChange,
  onValueChange,
  onReasonChange,
  onApply,
  onCancel,
}: InvoiceAdjustFieldsProps): JSX.Element {
  const zeroAmountDue = amountDueCents === 0;
  const zeroNoteId = `${idPrefix}-zero-note`;
  const describedBy = zeroAmountDue ? zeroNoteId : undefined;
  const ready = isAdjustReady(kind, value, reason, { zeroAmountDue, locked });
  return (
    <div
      className={`ferro-settle-now__adjust-form ferro-adjust-form${
        variant === 'inline' ? ' ferro-adjust-form--inline' : ''
      }`}
      data-testid={`${idPrefix}-form`}
    >
      <p className="ferro-field__label" id={`${idPrefix}-label`}>
        Adjust the price
      </p>
      {/* Ted #3 follow-up (#906) — what a percent applies to, said out loud.
          Without it a $0 visit looks the same as a $45 one until the server
          refuses. */}
      {amountDueCents !== null ? (
        <p className="ferro-settle-now__adjust-due" data-testid={`${idPrefix}-amount-due`}>
          Amount due on this {subject}: <strong>{formatMoney(amountDueCents)}</strong>
        </p>
      ) : null}
      {/* Ted #3 follow-up (#906) — the disabled-with-reason state. Plain
          language, and no cause we cannot prove from here (Principle 5). Two
          causes, two sentences: never priced (the Gingr-mirrored shape) vs
          comped to $0 on purpose, which the ledger above already shows —
          claiming the second "has no price yet" would contradict the very
          rows next to it. */}
      {zeroAmountDue ? (
        <p
          className="ferro-settle-now__adjust-note"
          id={zeroNoteId}
          data-testid={`${idPrefix}-zero-note`}
        >
          {deliberatelyZero
            ? `There is nothing due on this ${subject} right now, so a percent has nothing to work from and a discount would take it below zero. `
            : `This ${subject} has no price on it yet, so a percent has nothing to work from and a discount would take it below zero. `}
          {locked
            ? deliberatelyZero
              ? 'An owner or manager has to change the total.'
              : 'An owner or manager has to set the total first.'
            : 'Use New total to put a price on it, then discount that.'}
        </p>
      ) : null}
      <div
        className="ferro-settle-now__methods"
        role="radiogroup"
        aria-labelledby={`${idPrefix}-label`}
      >
        {KINDS.map((k) => (
          <button
            key={k.value}
            type="button"
            role="radio"
            aria-checked={kind === k.value}
            className={
              kind === k.value ? 'ferro-btn ferro-btn--primary' : 'ferro-btn ferro-btn--ghost'
            }
            onClick={() => onKindChange(k.value)}
            disabled={saving || locked || (k.value === 'percent' && zeroAmountDue)}
            aria-describedby={describedBy}
            data-testid={`${idPrefix}-kind-${k.value}`}
          >
            {k.label}
          </button>
        ))}
      </div>
      <label className="ferro-field">
        <span className="ferro-field__label">
          {kind === 'percent'
            ? 'Percent (e.g. -10 for 10% off)'
            : kind === 'amount'
              ? zeroAmountDue
                ? 'Amount in dollars (add to the price, e.g. 47.00)'
                : 'Amount in dollars (e.g. -9.40)'
              : 'New total in dollars'}
        </span>
        {/* $0 due: only a price going ON is accepted; a decrease is refused
            server-side as below_zero. */}
        <input
          type="number"
          step={kind === 'percent' ? '1' : '0.01'}
          min={zeroAmountDue ? '0' : undefined}
          className="ferro-field__input"
          value={value}
          onChange={(e) => onValueChange(e.target.value)}
          disabled={saving || locked}
          aria-describedby={describedBy}
          data-testid={`${idPrefix}-value`}
        />
      </label>
      <label className="ferro-field">
        <span className="ferro-field__label">Reason (required)</span>
        <input
          type="text"
          className="ferro-field__input"
          value={reason}
          onChange={(e) => onReasonChange(e.target.value)}
          placeholder="e.g. multi-dog 10%, grandfathered rate"
          maxLength={200}
          disabled={saving || locked}
          data-testid={`${idPrefix}-reason`}
        />
      </label>
      {hint ? (
        <p className="ferro-field__hint" data-testid={hintTestId ?? `${idPrefix}-hint`}>
          {hint}
        </p>
      ) : null}
      {error ? (
        <div className="ferro-dashboard__error" role="alert" data-testid={`${idPrefix}-error`}>
          <p>{error}</p>
        </div>
      ) : null}
      <div className="ferro-settle-now__actions">
        <button
          type="button"
          className="ferro-btn ferro-btn--primary"
          onClick={onApply}
          disabled={!ready || saving}
          aria-describedby={describedBy}
          data-testid={`${idPrefix}-apply`}
        >
          {saving ? 'Recording…' : 'Apply adjustment'}
        </button>
        <button
          type="button"
          className="ferro-btn ferro-btn--ghost"
          onClick={onCancel}
          disabled={saving}
          data-testid={`${idPrefix}-cancel`}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
